import { useReducer, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import { useCan, useIsAdmin } from "@/shared/auth";
import { toast } from "@/shared/lib/toast";

import { useListStock } from "../api/use-list-stock";
import { usePushStates, useResumePush, type PushState } from "../api/use-push";
import type { InventoryFilterState } from "../lib/url-state";

import { SyncMultiModal } from "./SyncMultiModal";

/**
 * Circuit-breaker banner (PRD v1.8 §4.6.3.2-C / A.7): shown while any
 * marketplace's push state carries `paused_at`. [Review] opens Sync Multi
 * pre-filled with every `out_of_stock` row for this marketplace — the rows
 * that need a re-push once the breaker's cleared — and resumes the push once
 * the send actually lands at least one success. [Resume push] resumes
 * directly, for the case where the operator fixed the root cause (stock
 * restored) and the loop should continue with the corrected numbers.
 *
 * The banner cannot be dismissed: a paused marketplace is not pushing stock,
 * and that stays visible until someone resumes it.
 *
 * The prefill is every row the server already returns for
 * `status: "out_of_stock"` — no client-side qty re-filter on top: per the
 * BE's stock-derived-columns design `out_of_stock` already means
 * `allocation_sellable == 0`, so a client-side `allocation_sellable <= 0`
 * check is always true and a `sellable > 0` check only discards the
 * genuinely-sold-out rows this flow exists to re-push. There's no
 * `last_synced_qty` field on `Stock` to filter by instead (see task report).
 */
export function BreakerBanner(): JSX.Element | null {
  const { t } = useTranslation("inventory");
  const canWriteStock = useCan("stock:write");
  const isAdmin = useIsAdmin();
  const { data } = usePushStates();
  const { mutate: resume, isPending: resuming } = useResumePush();
  const [reviewOpen, setReviewOpen] = useState(false);
  // Bumped on each [Review] click so SyncMultiModal remounts fresh — its
  // defaultSkus/defaultMarketplace are seeded once at mount (lazy initial
  // state), so a stale mount would otherwise keep showing a prior click's SKUs.
  const [reviewGeneration, bumpReviewGeneration] = useReducer((c: number) => c + 1, 0);

  const paused = (data?.items ?? []).filter((item): item is PushState & { paused_at: string } =>
    Boolean(item.paused_at),
  );
  const target = paused[0];

  // Only fetches once a marketplace is actually paused — not on every
  // Inventory page view.
  const reviewFilter: InventoryFilterState = {
    search: "",
    offset: 0,
    limit: 100, // the endpoint's maximum; see `truncated` below
    status: "out_of_stock",
    ...(target ? { marketplace: target.marketplace } : {}),
  };
  const { data: reviewStock, isLoading: reviewLoading } = useListStock(
    reviewFilter,
    Boolean(target),
  );

  if (!target) return null;

  const reviewSkus = (reviewStock?.items ?? [])
    .map((row) => row.sku)
    .filter((sku): sku is string => Boolean(sku));
  // More held rows than one page can carry: the operator has not reviewed
  // them all, so a successful send must NOT re-arm live push by itself
  // (review C2). They resume explicitly once the rest is handled.
  const heldTotal = reviewStock?.total ?? reviewSkus.length;
  const truncated = heldTotal > reviewSkus.length;

  const resumePush = (): void =>
    resume(
      { marketplace: target!.marketplace },
      { onError: (e) => toast.error(apiErrorMessage(e)) },
    );

  return (
    <>
      <div
        role="alert"
        data-testid="breaker-banner"
        className="mb-4 flex items-center justify-between gap-4 rounded-xl border border-[var(--intent-warning-fg)] bg-[var(--intent-warning-bg)] p-4"
      >
        <div>
          <p className="font-bold text-[var(--intent-warning-fg)]">
            {t("push.paused.title", { mkp: target.marketplace })}
          </p>
          <p className="text-sm text-[var(--text-secondary)]">
            {target.paused_reason
              ? t("push.paused.body", { reason: target.paused_reason })
              : t("push.paused.body_no_reason")}
          </p>
          {truncated && (
            <p
              data-testid="breaker-truncated"
              className="mt-1 text-xs text-[var(--text-secondary)]"
            >
              {t("push.paused.truncated", { shown: reviewSkus.length, total: heldTotal })}
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {canWriteStock && (
            <button
              type="button"
              data-testid="breaker-review"
              // Disabled while the out_of_stock rows are still loading — Sync
              // Multi seeds its SKU list once at mount, so opening it before
              // this resolves would prefill an empty list.
              disabled={reviewLoading}
              onClick={() => {
                bumpReviewGeneration();
                setReviewOpen(true);
              }}
              className="rounded-lg border border-[var(--premium-border-strong)] bg-white px-3.5 py-2 text-sm font-semibold text-[var(--text-primary)] transition disabled:cursor-not-allowed disabled:opacity-50 hover:bg-[var(--canvas)]"
            >
              {t("push.review")}
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              data-testid="breaker-resume"
              disabled={resuming}
              onClick={resumePush}
              className="rounded-lg px-3.5 py-2 text-sm font-semibold text-[var(--text-secondary)] transition disabled:cursor-not-allowed disabled:opacity-50 hover:bg-white/50"
            >
              {t("push.resume")}
            </button>
          )}
        </div>
      </div>
      <SyncMultiModal
        key={reviewGeneration}
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        defaultMarketplace={target.marketplace}
        defaultSkus={reviewSkus}
        onSynced={(result) => {
          // Only resume if the re-push actually landed at least one success —
          // a 200 whose items are all `ok: false` must not re-arm live push
          // to a marketplace that just rejected every write.
          //
          // Resume is admin-only on the BE (POST /v1/stock/push-state/{mkp}/resume),
          // so ops only sends the stock and is told an admin must re-arm push.
          if (!truncated && result.items.some((item) => item.ok)) {
            if (isAdmin) {
              resumePush();
            } else {
              toast.info(t("push.sent_admin_resume"));
            }
          }
          setReviewOpen(false);
        }}
      />
    </>
  );
}
